import axios from 'axios';
import { sanitizeCompany } from '../company/session';
import { isKnownCompany } from '../company/companies';
import { setFeatureOverrides } from '../company/featureOverrides';

// e.g. https://central.shelfve.com. Unset → no lookup: everyone gets the normal Mary login.
const CENTRAL_URL = (process.env.REACT_APP_CENTRAL_URL || '').trim().replace(/\/+$/, '');

/** True when this build looks emails up in central-service (email-first login). */
export const isCentralConfigured = Boolean(CENTRAL_URL);

const RESOLVE_TIMEOUT_MS = 5000;

/** Only real web origins may be redirected to (never javascript:, data:, …). */
function safeAdminUrl(value) {
    try {
        const url = new URL(value);
        const local = ['localhost', '127.0.0.1'].includes(url.hostname);
        if (url.protocol !== 'https:' && !(local && url.protocol === 'http:')) return null;
        url.hash = '';
        return url.href;
    } catch {
        return null;
    }
}

/**
 * Decide where this email signs in from central-service's answer. Pure (see central.test.js).
 *
 *   { kind: 'company', company }              a company this admin serves (company/companies.js):
 *                                             sign in here, at company.apiUrl
 *   { kind: 'legacy' }                        no usable answer: the classic Mary login
 *   { kind: 'redirect', url, companyName }    a company this admin does not serve, with its own
 *                                             admin; url carries the email in the #fragment
 *   { kind: 'elsewhere', companyName }        such a company without an admin URL
 *
 * @param {string} email
 * @param {{ status?: number, data?: any } | null} answer - null for a network error / timeout
 */
export function decideCompany(email, answer) {
    if (!answer || answer.status !== 200) return { kind: 'legacy' };
    const data = answer.data || {};
    if (!data.companyId) return { kind: 'legacy' };

    const company = sanitizeCompany({ companyId: data.companyId, apiUrl: data.apiUrl, companyName: data.companyName });
    if (company) return { kind: 'company', company };

    const companyName = typeof data.companyName === 'string' ? data.companyName : '';
    const adminUrl = safeAdminUrl(data.adminUrl);
    if (!adminUrl) return { kind: 'elsewhere', companyName };
    // Fragment, never a query string: it isn't sent to servers, so the email stays out of
    // access logs and Referer headers.
    return { kind: 'redirect', url: `${adminUrl}#email=${encodeURIComponent(email)}`, companyName };
}

/**
 * Which company does this email sign in to? See `decideCompany` for the answers.
 *
 * Anything other than a clean 200 (404 unknown email, 429, network error, timeout, central-service
 * not configured) is `legacy`: the classic Mary login, so Mary keeps working when central-service
 * is down. Either way the password then goes to exactly one backend.
 *
 * The request carries only the email: no token, cookies or custom headers (central-service's CORS
 * allows Content-Type only).
 */
export async function resolveCompany(email) {
    const typed = String(email || '').trim();
    if (!CENTRAL_URL || !typed) return { kind: 'legacy' };

    let answer = null;
    try {
        const res = await axios.post(
            `${CENTRAL_URL}/v1/resolve`,
            { email: typed },
            { timeout: RESOLVE_TIMEOUT_MS, withCredentials: false, headers: { 'Content-Type': 'application/json' } }
        );
        answer = { status: res?.status, data: res?.data };
    } catch {
        answer = null;
    }
    return decideCompany(typed, answer);
}

/**
 * Reads `#email=` (set by another company's admin when it sends a user here), then removes the
 * fragment from the address bar. Returns the email or ''.
 */
export function takeEmailFromHash() {
    if (typeof window === 'undefined' || !window.location.hash) return '';
    const email = new URLSearchParams(window.location.hash.slice(1)).get('email') || '';
    if (!email) return '';
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    return email.trim();
}

const FEATURES_TIMEOUT_MS = 5000;
const FEATURES_REFRESH_MS = 5 * 60 * 1000;
const lastFeaturesFetch = new Map(); // companyId -> ms

/**
 * Fetch `companyId`'s feature flags from central-service and apply them
 * (company/featureOverrides.js). At most once per 5 minutes per company unless `force`.
 * A network failure keeps the current values; a 404 (company gone or inactive) drops them,
 * back to the built-in values. Never throws.
 * @param {string} companyId
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<boolean>} Whether any flag changed
 */
export async function refreshFeatureFlags(companyId, { force = false } = {}) {
    if (!CENTRAL_URL || !isKnownCompany(companyId)) return false;
    const now = Date.now();
    if (!force && now - (lastFeaturesFetch.get(companyId) || 0) < FEATURES_REFRESH_MS) return false;
    lastFeaturesFetch.set(companyId, now);
    try {
        const res = await axios.get(`${CENTRAL_URL}/v1/companies/${encodeURIComponent(companyId)}/features`, {
            timeout: FEATURES_TIMEOUT_MS,
            withCredentials: false,
        });
        if (res?.data?.companyId !== companyId) return false;
        return setFeatureOverrides(companyId, res.data.features);
    } catch (err) {
        if (err?.response?.status === 404) return setFeatureOverrides(companyId, null);
        lastFeaturesFetch.delete(companyId); // retry on the next chance
        return false;
    }
}
